import { Ref, useLayoutEffect, useState } from 'react'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { NavSectionLabel, SideNav, Skeleton, ToggleButton } from '@repo/ui'

import { ASK_PANEL_SLIDE_DURATION_MS, ASK_PANEL_SLIDE_EASE } from 'ask'
import { Search } from 'components'

import { LABS_CONTENT_TOP, LABS_TOC_W, labsStickyColumnHeight } from '../constants'

/**
 * The head rule's fade, borrowed from the app's page-slide curve rather than invented.
 *
 * Imported rather than redeclared because `NotificationsLab` — a sibling inside this very
 * `LabsPage` folder — already imports the same two constants from `ask`, so a local copy
 * here would be the third statement of one curve within one feature. (`SetupChecklist`
 * keeps local copies on a dependency-direction argument; that argument is about Home,
 * which has no other reason to reach into `ask/`. This folder already does.)
 */
const RULE_FADE = `${ASK_PANEL_SLIDE_DURATION_MS}ms ${ASK_PANEL_SLIDE_EASE}`

/**
 * The scope track's rendered height, and the search field's — both measured, not chosen.
 *
 * They exist as constants so `LabsSideColumnSkeleton` can reserve the real footprint
 * rather than an eyeballed one (§19). A skeleton that mirrors the layout it replaces is
 * only worth anything if its numbers come from the same place the layout's do.
 */
const SCOPE_H = 48
const SEARCH_H = 50

export interface LabsSideColumnGroup {
  category: string
  items: { label: string; value: string }[]
}

interface LabsSideColumnProps {
  /** The section's scope filter — 2–3 mutually-exclusive options, as a segmented control. */
  scope: {
    options: { label: string; value: string }[]
    value: string
    onChange: (value: string) => void
  }
  search: {
    value: string
    onChange: (value: string) => void
    placeholder: string
  }
  /** The index, already grouped and ordered by the caller. */
  groups: LabsSideColumnGroup[]
  activeValue: string
  onSelect: (value: string) => void
  /** Shown in place of the index when `groups` is empty. */
  emptyText: string
  /**
   * The column's own outer element, for a caller that needs to MEASURE it.
   *
   * `ComponentsLab`'s scroll-spy is the one caller: the column pins at exactly the page
   * header's bottom edge, so its rect is the bottom of the occluded strip, which is the
   * line the spy has to trigger below. Exposed as a ref rather than letting the caller
   * wrap this in its own `<Box ref>` — a wrapper would either become the sticky element's
   * containing block (unpinning it as soon as the wrapper scrolled away) or need
   * `display: contents`, which has NO BOX and therefore returns a zero
   * `getBoundingClientRect`, i.e. a silently wrong measurement rather than an error.
   */
  containerRef?: Ref<HTMLDivElement>
}

/**
 * A Labs section's left column: scope toggle → search → grouped index.
 *
 * ## Why the scope filter lives HERE and not in a sticky band (VRG-560)
 *
 * Both sections used to head themselves with a horizontal sticky band carrying a `Tabs`
 * row — "App | Platform" on Notifications, "Common | Platform | App" on Components. Two
 * things were wrong with that, and only the second is about layout.
 *
 * **It mis-described the control.** Those rows are not page navigation; they are a FILTER
 * on the list immediately below them, and they change nothing the reader can see except
 * that list. A control belongs with the thing it acts on, so it goes in the column, above
 * the search field it composes with — the two together are "which subset of the index am
 * I looking at". Tabs also carry a promise this control does not keep: a tab row implies
 * distinct destinations, while these are two views of one list. A segmented `ToggleButton`
 * says "pick one of these" without implying a page change.
 *
 * **It cost a second sticky level.** A band pinned below the page header meant everything
 * further down pinned below header + band, i.e. against a SUM of two runtime measurements
 * (`--labs-header-h` + `--labs-band-h`). Four consecutive commits — 663aa9615, d685e1fd8,
 * ef214e4ac, 06f104ef0 — went at that geometry, and each had to make the band's `top`, the
 * band's `pb`, this column's `top` and this column's height agree at once. Moving the
 * filter into the column deletes the middle term outright: one sticky level, pinned at one
 * published number. See `LABS_CONTENT_TOP`.
 *
 * ## What deliberately does NOT go in this column
 *
 * The appearance (light/dark) toggle, and Notifications' "Hide panel". Both used to ride
 * in the band alongside the tabs, and the temptation on deleting the band is to sweep them
 * in here — which would repeat the exact mistake above one level down. Neither acts on the
 * index: the appearance toggle re-renders the artifact in the middle column, and "Hide
 * panel" sizes it. They live beside that artifact, in the preview column's own control
 * row. This column holds only what filters or selects from the list it contains.
 *
 * ## Why it is a shared component
 *
 * §36 — the construction below (viewport-capped sticky column, non-scrolling head over an
 * internally-scrolling index, the paint-order fill, the negative/positive inset pair) was
 * written for `NotificationsLab`, and `ComponentsLab` needs exactly it. That is the second
 * hand-roll, so it is extracted rather than copied. The sections differ only in their
 * scope options and in what selecting a row does.
 */
export const LabsSideColumn = ({
  scope,
  search,
  groups,
  activeValue,
  onSelect,
  emptyText,
  containerRef,
}: LabsSideColumnProps) => {
  /**
   * Whether the index below the head is scrolled off its own top — the ONLY input to the
   * head's rule (see the `borderBottom` below).
   *
   * ⚠️ There is deliberately no `IntersectionObserver` sentinel and no sticky-detection
   * trick here, and the reason is structural rather than stylistic: **the head is not
   * `position: sticky`**. It is `flexShrink: 0` inside a capped flex column, so it is
   * pinned at every scroll offset and there is no "became stuck" moment to observe. The
   * question the rule answers is not "is the head stuck" but "has the list moved", which
   * is `scrollTop > 0` on the scroller and nothing else.
   *
   * ## ⚠️ This cannot be verified by scrolling in the Claude preview pane — MEASURED
   *
   * The pane does not run the event loop's "update the rendering" step, and every symptom
   * follows from that one fact. Measured here at 1280x800 on both Labs sections:
   *
   * - **No `scroll` event is ever emitted.** A bare `addEventListener('scroll', …)` on the
   *   scroller counted **0** callbacks after `scrollTop = 250`, and a real wheel gesture
   *   over the list timed out without moving `scrollTop` at all. So an observer is not the
   *   thing that fails here — `scroll` itself does. Anyone concluding "the rule is broken"
   *   from scrolling this list in the pane has measured the pane, not the code.
   * - **The transition never advances.** `head.getAnimations()` returns **0**, so
   *   `getComputedStyle().borderBottomColor` stays pinned at the transition's START value
   *   even after the state has flipped and the emotion class has been swapped. A reader
   *   sampling the computed colour sees `rgba(0,0,0,0)` in BOTH states and concludes the
   *   handler never ran, which is the wrong conclusion twice over.
   *
   * To verify it here: dispatch `new Event('scroll', { bubbles: true })` on the scroller —
   * **`bubbles: true` is required**, React 18 registers this as `scroll__bubble` and a
   * non-bubbling synthetic event does not reach the handler — then read the colour with
   * `style.transition = 'none'` to skip the frozen interpolation. Doing that yields
   * `rgba(0,0,0,0)` at top, `rgba(0,0,0,0.12)` (the `divider` token) scrolled, and
   * `rgba(0,0,0,0)` back at top, with the head's height and the scroller's top identical
   * in all three (119 / 297) — i.e. the mechanism is right and only the pane's playback is
   * missing.
   */
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null)
  const [scrolled, setScrolled] = useState(false)

  /**
   * Re-read after the list's CONTENT changes — typing in the search field can shrink the
   * index below the scroll threshold, at which point the browser clamps `scrollTop` to 0
   * on its own. A clamp is not reliably delivered as a `scroll` event, so without this the
   * rule could stay drawn over a list that no longer scrolls.
   *
   * Callback ref rather than `useRef`, matching `usePublishedHeight` in `../constants`:
   * the scroller is a stable node here, but storing it in state is what makes this effect
   * re-run when it attaches rather than depending on a first-run race.
   */
  useLayoutEffect(() => {
    if (scroller) setScrolled(scroller.scrollTop > 0)
  }, [scroller, groups])

  return (
    /**
     * VIEWPORT-HEIGHT COLUMN with the index scrolling INSIDE it, the same construction as
     * `DocSectionNav`. `position: sticky` alone is not enough: a sticky element TALLER than
     * the viewport does not pin at `top`, it rides up with the page until its own bottom
     * reaches the viewport bottom — so with 47 templates the search field would scroll away,
     * which is exactly the failure this arrangement exists to prevent. Capping the column
     * turns the overflow into an internal scroll.
     */
    <Box
      ref={containerRef}
      sx={{
        width: LABS_TOC_W,
        flexShrink: 0,
        position: 'sticky',
        top: LABS_CONTENT_TOP,
        height: labsStickyColumnHeight(48),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── The column's non-scrolling head: scope, then search ──────────────────
          `flexShrink: 0` is what keeps it out of the scroll — it is the reason the column is
          capped at all — but on its own that was NOT enough to keep index rows from showing
          through it, and the fix is the fill and the stacking, not the flex.

          Measured on the Notifications index: the rows are MUI `MenuItem`s, i.e.
          `position: relative` with `z-index: auto`, and they sit LATER in DOM order than
          this block inside the column's own stacking context (the column is
          `position: sticky`). So they paint ABOVE the search field, and the only thing
          holding them back is the scroller's overflow clip — which does not cover this
          block, because the scroller is deliberately 8px wider on each side (`mx: -2`, so
          the rail's selected-row mark has room to paint without being clipped). Rows at the
          top of the list were measured occupying y 229-256 while the field's box ran
          215-265, i.e. geometrically inside it, held off by clipping alone.

          So: an explicit `background.default` fill spanning the scroller's FULL width (the
          same `mx: -2 / px: 2` inset pair, so the fill reaches the strips the clip does
          not), and `position: relative` + `zIndex: 1` to win the paint order outright rather
          than depend on a clip rectangle 16px narrower than the thing it is protecting.
          `pb: 2` puts a filled 8px strip below the field so a row scrolling up disappears
          under solid colour instead of touching its border. */}
      <Stack
        spacing={3}
        sx={{
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
          bgcolor: 'background.default',
          mx: -2,
          px: 2,
          pb: 2,
          /* The head is the only thing pinned, so nothing else marks where it ends —
             a row scrolling under it just vanishes. The rule gives the boundary an
             edge. It spans the scroller's FULL width via the same `mx: -2` the fill
             uses, so it reads as a divider rather than an underline on the field.

             It is drawn ONLY while the index below is scrolled. At rest there is
             nothing hidden under the head, so a rule there is an edge marking a
             boundary nothing has crossed — it reads as a seam in the chrome rather
             than as information. Once a row has passed underneath, the same line is
             telling you where the list was cut off, which is worth drawing.

             ⚠️ The border BOX is present in both states and only its COLOUR changes.
             Toggling `borderBottom` between `none` and `1px` would resize the head by
             1px on the first scroll and shift the whole index under the cursor —
             which is the layout jump §19 exists to prevent, arriving from the other
             direction. `transparent` keeps the 1px reserved and paints nothing. */
          borderBottom: '1px solid',
          borderColor: scrolled ? 'divider' : 'transparent',
          transition: `border-color ${RULE_FADE}`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {/* ⚠️ Deliberately NOT `fullWidth`, and the reason is a defect in the shared
            component rather than a preference.

            `ToggleButton`'s `fullWidth` docblock says it will "stretch to the container and
            split the halves evenly". It does the first and not the second: the implementation
            is `width: fullWidth ? "100%" : "fit-content"` on the WRAPPER only, and the
            `Tab`s inside stay shrink-wrapped. Measured here at `LABS_TOC_W`: the track came
            out 288 wide with tabs of 60.5 ("App") and 93.8 ("Platform") left-aligned inside
            it, i.e. ~130px of empty track — which is precisely the "floats against the left
            edge with dead space beside it" look that docblock says `fullWidth` exists to
            prevent. Documentation describing an intent the program does not implement, which
            is the same failure class as this folder's own `LABS_HEADER_H = 64` and the
            context whose default was `0`.

            It is NOT fixed here. The only two existing `fullWidth` call sites are
            `apps/app`'s `DisplayedDataPopover` and `MyCode` — mobile bottom-sheet surfaces
            that must be verified at 375x812 (§37), which this change has no running app to
            do. Correcting layer 1 would silently restyle two surfaces nobody looked at, so
            it is reported instead (§50 — found work is done or recorded, never guessed at).

            Fit-content is also simply what every platform call site already uses, so the
            control matches Company General's and Studio's. At 288px the widest option set
            here ("Common | Platform | App") measures well under the column, so nothing
            truncates (§49). */}
        <ToggleButton tabs={scope.options} tab={scope.value} onChange={scope.onChange} />
        <Search
          placeholder={search.placeholder}
          value={search.value}
          onChange={search.onChange}
          // The list is local and at most ~50 rows, so a debounce buys nothing and costs the
          // filter a visible lag behind the keystroke.
          debounce={false}
          fullWidth
          // Overrides the shared field's 300px floor, which is sized for a search above a
          // data table and would otherwise force this column wide.
          sx={{ minWidth: 0 }}
        />
      </Stack>

      {/* The negative/positive inset pair gives the rail's selected-row mark room to paint
          without the scroll box clipping it. `minHeight: 0` is load-bearing: a flex child's
          default `min-height: auto` refuses to shrink below its content and silently
          reinstates the tall-column behaviour the cap exists to prevent. */}
      <Box
        ref={setScroller}
        onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 0)}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', mx: -2, px: 2 }}
      >
        {groups.length ? (
          groups.map(({ category, items }, gi) => (
            // 16px between the head and the first group: chrome and index are different
            // KINDS of thing, and at the label's own gap the search field read as the list's
            // first row. 4 (16px) not 6, because the head above contributes the other 8 as
            // its own filled `pb` — the visible gap is 24.
            <Box key={category} sx={{ mt: gi === 0 ? 4 : 0 }}>
              {/* The shared section label, so this heading cannot drift from the settings
                  nav's and the sidebar's (§36). It WRAPS rather than truncating — a category
                  rendering as "SIG…" in the old horizontal chip row is half of why this
                  column exists (§49). */}
              <NavSectionLabel first={gi === 0}>{category}</NavSectionLabel>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ pl: 2 }}>
                <SideNav variant="rail" items={items} value={activeValue} onChange={onSelect} />
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 6, px: 3 }}>
            {emptyText}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/**
 * Footprint-parity skeleton for `LabsSideColumn` (§19).
 *
 * It lives in this file, beside the component it stands in for, and reads the SAME
 * `LABS_TOC_W` / `SCOPE_H` / `SEARCH_H` / spacing values — so the two cannot drift into
 * disagreement, which is the only way a skeleton causes the layout shift it exists to
 * prevent. The head is reproduced control-for-control (scope track, then field) inside the
 * identical `Stack spacing={3}` and `pb: 2`, because the gap between them is part of the
 * footprint just as much as the boxes are.
 *
 * PADDING, not margin, on that `pb` — and the difference is not cosmetic. An 8px bottom
 * MARGIN here would sit adjacent to the first group's `mt: 4` and the two would COLLAPSE
 * to 16 rather than summing to 24, putting the skeleton's first divider 8px above the
 * loaded one (measured 355 vs 363 when this was last got wrong). The loaded head spends
 * its 8 as padding, so it never collapses; this must do the same.
 *
 * The group rhythm below is measured against the loaded column rather than eyeballed: a
 * `NavSectionLabel` renders a 26px box, its `Divider` has `mt: 0` and `mb: 2`, and a rail
 * row is 27.5px and contiguous with its neighbours. An earlier version drew an 11px bar
 * where the label is 26 and put an 8px `mt` on a divider whose real `mt` is 0, and came
 * out 15px short at the first divider.
 */
export const LabsSideColumnSkeleton = ({
  groups = 3,
  rows = 4,
  /**
   * The scope track's rendered WIDTH. 170 is the measured "App | Platform" track, which is
   * the only section that has a loading state today.
   *
   * It is a prop and not a constant because the track is `fit-content` (see the
   * `fullWidth` note above), so its width is a function of the option labels — a section
   * with a different set measures differently, and a full-width bar here would promise a
   * full-width control that never arrives. Footprint parity is about shape as well as box
   * (§19); the HEIGHT is what prevents the shift, the width is what stops the placeholder
   * lying about what it stands in for.
   */
  scopeWidth = 170,
}: {
  groups?: number
  rows?: number
  scopeWidth?: number
}) => (
  <Box sx={{ width: LABS_TOC_W, flexShrink: 0 }}>
    {/* The transparent rule is the loaded head's REST state, so the skeleton carries it
        too — it is 1px of footprint (§19), and without it the first group sits 1px higher
        under the skeleton than under the real column. Never the visible `divider` colour:
        a skeleton has no scrolled state to report. */}
    <Stack spacing={3} sx={{ pb: 2, borderBottom: '1px solid transparent' }}>
      <Skeleton variant="rounded" width={scopeWidth} height={SCOPE_H} />
      <Skeleton variant="rounded" width="100%" height={SEARCH_H} />
    </Stack>
    {Array.from({ length: groups }).map((_, g) => (
      <Box key={g} sx={{ mt: g === 0 ? 4 : 5 }}>
        <Box sx={{ height: 26, display: 'flex', alignItems: 'center' }}>
          <Skeleton variant="rounded" width={72} height={11} />
        </Box>
        <Divider sx={{ mt: 0, mb: 2 }} />
        <Box>
          {Array.from({ length: rows }).map((__, r) => (
            <Box key={r} sx={{ height: 27.5, display: 'flex', alignItems: 'center', pl: 3 }}>
              <Skeleton variant="rounded" width={`${88 - r * 9}%`} height={14} />
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
)
